import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Calendar, Clock, MapPin, Mail, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getPaymentStatus, getBooking } from '../utils/api';
import { toast } from 'sonner';

const Confirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const bookingId = searchParams.get('booking_id');

    if (!sessionId || !bookingId) {
      navigate('/');
      return;
    }

    // Poll payment status
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      
      if (attempts >= maxAttempts) {
        toast.error('Payment status check timed out. Please contact us.');
        setLoading(false);
        return;
      }

      try {
        const paymentStatus = await getPaymentStatus(sessionId);
        
        if (paymentStatus.paymentStatus === 'paid') {
          // Payment confirmed, fetch booking details
          const booking = await getBooking(bookingId);
          setBookingData(booking);
          setPaymentConfirmed(true);
          setLoading(false);
          toast.success('Payment successful! Your booking is confirmed.');
        } else if (paymentStatus.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setLoading(false);
          navigate('/booking');
        } else {
          // Still pending, poll again
          setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        toast.error('Error verifying payment. Please contact us.');
        setLoading(false);
      }
    };

    pollPaymentStatus();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-zinc-400 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-zinc-100 mb-2">Confirming Your Payment</h2>
          <p className="text-zinc-400">Please wait while we process your booking...</p>
        </div>
      </div>
    );
  }

  if (!paymentConfirmed || !bookingData) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h2 className="text-2xl font-bold text-zinc-100 mb-4">Unable to Confirm Booking</h2>
          <p className="text-zinc-400 mb-6">
            We're having trouble confirming your payment. Please contact us with your booking reference.
          </p>
          <Button onClick={() => navigate('/')} className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/10 rounded-full mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-zinc-100">Booking Confirmed!</h1>
          <p className="text-xl text-zinc-400">
            Your appointment has been successfully scheduled
          </p>
          <p className="text-sm text-zinc-500 mt-2">
            Booking ID: <span className="font-mono text-zinc-300">{bookingData.bookingId}</span>
          </p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800 mb-8">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold text-zinc-100 mb-6">Appointment Details</h2>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                  <Calendar className="w-5 h-5 text-zinc-400" />
                </div>
                <div>
                  <div className="text-sm text-zinc-500 mb-1">Service</div>
                  <div className="text-lg font-semibold text-zinc-100">{bookingData.serviceName}</div>
                  <div className="text-sm text-zinc-400">€{bookingData.totalAmount}</div>
                  {bookingData.addOns && bookingData.addOns.length > 0 && (
                    <div className="mt-2 text-xs text-zinc-500">
                      Add-ons: {bookingData.addOns.map(a => a.name).join(', ')}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                  <Clock className="w-5 h-5 text-zinc-400" />
                </div>
                <div>
                  <div className="text-sm text-zinc-500 mb-1">Date & Time</div>
                  <div className="text-lg font-semibold text-zinc-100">
                    {new Date(bookingData.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="text-sm text-zinc-400">{bookingData.time}</div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                  <MapPin className="w-5 h-5 text-zinc-400" />
                </div>
                <div>
                  <div className="text-sm text-zinc-500 mb-1">Location</div>
                  <div className="text-lg font-semibold text-zinc-100">{bookingData.address}</div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                  <Mail className="w-5 h-5 text-zinc-400" />
                </div>
                <div>
                  <div className="text-sm text-zinc-500 mb-1">Confirmation Email</div>
                  <div className="text-lg font-semibold text-zinc-100">{bookingData.email}</div>
                  <div className="text-sm text-zinc-400">Check your inbox for details</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800 mb-8">
          <h3 className="font-semibold text-zinc-100 mb-3">What's Next?</h3>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li>✓ You'll receive a confirmation email shortly</li>
            <li>✓ We'll send a reminder 24 hours before your appointment</li>
            <li>✓ Our team will arrive at your location on time</li>
            <li>✓ Please ensure the vehicle is accessible</li>
          </ul>
        </div>

        <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800 mb-8">
          <h3 className="font-semibold text-zinc-100 mb-3">Need to Make Changes?</h3>
          <p className="text-sm text-zinc-400 mb-4">
            Contact us at least 24 hours in advance to reschedule or cancel your appointment free of charge.
          </p>
          <p className="text-sm text-zinc-300">
            Phone: <span className="font-semibold">+351 96 98 52 004</span>
          </p>
          <p className="text-sm text-zinc-300">
            Email: <span className="font-semibold">info@alinsideout.com</span>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button 
            onClick={() => navigate('/')} 
            variant="outline" 
            className="flex-1 bg-zinc-950 text-zinc-300 border-zinc-800 hover:bg-zinc-800"
          >
            Back to Home
          </Button>
          <Button 
            onClick={() => navigate('/booking')} 
            className="flex-1 bg-zinc-100 text-zinc-950 hover:bg-zinc-200"
          >
            Book Another Appointment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
